import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Session cookies require credentialed requests (HttpOnly cookie, not localStorage)
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Migrate away from legacy JWT-in-localStorage
    localStorage.removeItem("token");

    const initAuth = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          withCredentials: true,
        });
        setUser(response.data);
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(
      `${API_URL}/api/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    const userData = response.data.user;
    setUser(userData);
    localStorage.removeItem("token");
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Still clear local UI state if network/logout endpoint fails
      console.error("Logout request failed:", error);
    }
    localStorage.removeItem("token");
    setUser(null);
  };

  const getAuthHeader = () => {
    // Auth is cookie-based; keep helper for existing call sites
    return {};
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token: null,
        loading,
        login,
        logout,
        getAuthHeader,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
